import { parse } from 'path-browserify';

import {
  httpGetAssetsByAssetId,
  httpPostCollectionsByCollectionIdContents,
} from '@iconik-io/iconik-api/src/services/assets';
import { httpGetFilesChecksumByChecksum } from '@iconik-io/iconik-api/src/services/files';
import { isNotFoundError } from '@iconik-io/iconik-api/src/utils/is_not_found_error';
import { unwrapResponseBody } from '@iconik-io/iconik-api/src/utils/unwrap_response_body';

import type { EntitiesLifecycleContext } from './entities_lifecycle/entities_lifecycle';

const CHECKSUM_LOOKUP_PAGE_SIZE = 100;

export async function calculateUploadChecksum<Meta, ChunkMeta>(
  context: EntitiesLifecycleContext<Meta, ChunkMeta>,
): Promise<string | undefined> {
  const state = context.getState();
  if (state.checksum) {
    return state.checksum;
  }
  const { calculateChecksum } = context;
  if (
    !calculateChecksum ||
    state.uploadKind.kind !== 'file' ||
    !state.options.calcUploadChecksum
  ) {
    return undefined;
  }

  const { streamSource, targetPath } = state.fileInfo;
  context.emit('log', {
    level: 'info',
    message: `Calculating checksum for ${targetPath}`,
    state,
  });
  const checksum = await calculateChecksum(
    streamSource,
    context.abortList.newSignal(),
  );
  context.updateState({ checksum });
  context.emit('log', {
    level: 'info',
    message: `Got checksum ${checksum} for ${targetPath}`,
    state: context.getState(),
  });
  return checksum;
}

type ReusableAsset = {
  id: string;
  collectionIds: string[];
};

async function getActiveAsset<Meta, ChunkMeta>(
  context: EntitiesLifecycleContext<Meta, ChunkMeta>,
  assetId: string,
): Promise<ReusableAsset | undefined> {
  try {
    const asset = await unwrapResponseBody(
      context.apiClient.request(
        httpGetAssetsByAssetId(assetId, { include_collections: true }),
        { abortSignal: context.abortList.newSignal() },
      ),
    );
    if (asset.status === 'DELETED') {
      return undefined;
    }
    return { id: assetId, collectionIds: asset.in_collections ?? [] };
  } catch (error) {
    if (isNotFoundError(error)) {
      return undefined;
    }
    throw error;
  }
}

async function findAssetByChecksum<Meta, ChunkMeta>(
  context: EntitiesLifecycleContext<Meta, ChunkMeta>,
  checksum: string,
): Promise<ReusableAsset | undefined> {
  const requiredStorageId =
    context.getState().options.checksumMatch === 'sameStorage'
      ? context.storageOptions.id
      : undefined;
  const checkedAssetIds = new Set<string>();
  let lastId: string | undefined;
  let hasMorePages = true;
  while (hasMorePages) {
    const response = await unwrapResponseBody(
      context.apiClient.request(
        httpGetFilesChecksumByChecksum(checksum, {
          per_page: CHECKSUM_LOOKUP_PAGE_SIZE,
          last_id: lastId,
        }),
        { abortSignal: context.abortList.newSignal() },
      ),
    ).catch((error) => {
      // The endpoint answers 404 when no file has this checksum
      if (isNotFoundError(error)) {
        return undefined;
      }
      throw error;
    });
    if (!response) {
      return undefined;
    }
    const files = response.objects ?? [];

    for (const file of files) {
      const assetId = file.asset_id;
      if (
        !assetId ||
        checkedAssetIds.has(assetId) ||
        file.checksum !== checksum ||
        (requiredStorageId !== undefined &&
          file.storage_id !== requiredStorageId) ||
        file.status !== 'CLOSED'
      ) {
        continue;
      }
      checkedAssetIds.add(assetId);
      // Deleting an asset leaves its files in place until they are purged, so
      // a file can look ok, but the parent asset is gone
      const asset = await getActiveAsset(context, assetId);
      if (asset) {
        return asset;
      }
    }

    const nextLastId = files.at(-1)?.id ?? undefined;
    hasMorePages =
      files.length === CHECKSUM_LOOKUP_PAGE_SIZE &&
      nextLastId !== undefined &&
      nextLastId !== lastId;
    lastId = nextLastId;
  }
  return undefined;
}

export async function reuseAssetByChecksum<Meta, ChunkMeta>(
  context: EntitiesLifecycleContext<Meta, ChunkMeta>,
): Promise<boolean> {
  const { target, options, uploadKind, fileInfo, checksum } =
    context.getState();
  if (
    !checksum ||
    !options.checksumMatch ||
    options.checksumMatch === 'off' ||
    uploadKind.kind !== 'file' ||
    target.type !== 'newAsset' ||
    target.assetId
  ) {
    return false;
  }

  const asset = await findAssetByChecksum(context, checksum);
  if (!asset) {
    return false;
  }

  const destinationCollection =
    await context.collectionsManager.getOrCreateCollection(
      parse(fileInfo.targetPath).dir,
      options.baseCollectionId,
      context.abortList,
      { applyAcls: options.collectionACL },
    );
  const addToCollection =
    destinationCollection !== undefined &&
    !asset.collectionIds.includes(destinationCollection.collectionId);
  if (addToCollection) {
    await context.apiClient.request(
      httpPostCollectionsByCollectionIdContents(
        destinationCollection.collectionId,
        { object_id: asset.id, object_type: 'assets' },
        { index_immediately: true },
      ),
      { abortSignal: context.abortList.newSignal() },
    );
  }

  context.updateState({
    target: { type: 'existingAsset', assetId: asset.id },
    skippedTransferForChecksum: true,
  });
  context.emit('log', {
    level: 'info',
    message: `Found asset ${asset.id} with checksum ${checksum}, ${addToCollection ? 'added it to' : 'already in'} collection ${destinationCollection?.collectionId ?? '(none)'} instead of uploading`,
    state: context.getState(),
  });
  return true;
}
